/**
 * Icons of the workspace demo that have no match in lucide: the agent glyphs, the gradient marks
 * and avatars, the app logos and a few line icons — drawn after the reference image.
 */
import { useId, type ReactNode, type SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

/** A 24-grid line icon in the same hand as lucide (round caps and joins). */
function Line({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...rest}>
      {children}
    </svg>
  )
}

/* ── agent glyphs: a ring of eight dots around a glowing core ─────────── */

export type AgentName = 'rune' | 'aether' | 'syntax' | 'theo' | 'clara'

const tint = (c: string) => Array<string>(8).fill(c)
const AGENTS: Record<AgentName, { ring: string[]; core: [string, string, string, string] }> = {
  // The ring (eight beads) runs clockwise from the top; the core runs from its upper left to its lower right.
  rune: { ring: ['#9fe8c8', '#fbf7e6', '#ffd29a', '#fcf4e2', '#b6efcc', '#f0eef8', '#bfaef8', '#aec2f8'], core: ['#9868d2', '#ee82b4', '#ffae74', '#ffeaa6'] },
  aether: { ring: tint('#e6d2dc'), core: ['#f9cbe2', '#f5b4d4', '#eea8ca', '#e6a3c6'] },
  syntax: { ring: tint('#d0d6d4'), core: ['#ffffff', '#ececf0', '#d6dad9', '#c8cecd'] },
  theo: { ring: ['#e6e8c0', ...tint('#d0d6da').slice(1)], core: ['#e9f5ea', '#b4d8e6', '#8cc0e0', '#7fb2d8'] },
  clara: { ring: tint('#d4cde6'), core: ['#e8d4fc', '#d2a8f4', '#c09ee8', '#b89be0'] },
}

export function AgentGlyph({ agent, size = 16, dim }: { agent: AgentName; size?: number; dim?: boolean }) {
  const id = useId()
  const a = AGENTS[agent]
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden className="shrink-0" opacity={dim ? 0.72 : 1}>
      <defs>
        <linearGradient id={id} x1="0.36" y1="0" x2="0.64" y2="1">
          <stop offset="0" stopColor={a.core[0]} />
          <stop offset="0.38" stopColor={a.core[1]} />
          <stop offset="0.68" stopColor={a.core[2]} />
          <stop offset="1" stopColor={a.core[3]} />
        </linearGradient>
      </defs>
      {a.ring.map((c, i) => (
        <circle key={i} cx={8} cy={2} r={1.05} fill={c} transform={`rotate(${i * 45} 8 8)`} />
      ))}
      <circle cx={8} cy={8} r={3.7} fill={`url(#${id})`} />
    </svg>
  )
}

/* ── gradient marks ──────────────────────────────────────────────────── */

/** The workspace's mark: a soft square that runs blue → pink → amber. */
export function WorkspaceMark({ size = 17 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="block shrink-0 rounded-[5px]"
      style={{ width: size, height: size, background: 'linear-gradient(166deg, #a0dcdc 0%, #9fd6e8 18%, #86a6e4 30%, #a48cee 40%, #cc78d4 48%, #f07e98 57%, #ffae7c 66%, #ffd292 78%, #f8e8c4 90%, #f2f6ee 100%)' }}
    />
  )
}

/** The signed-in user's avatar. */
export function UserAvatar({ size = 18 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="block shrink-0 rounded-full"
      style={{ width: size, height: size, background: 'linear-gradient(198deg, #a7a2f6 4%, #d279e4 26%, #ff72b4 46%, #ffa58c 62%, #ffdcaa 78%, #fff6c4 100%)' }}
    />
  )
}

const PEOPLE = [
  'linear-gradient(100deg, #ffc6a4 0%, #fbd3bf 42%, #f1e9ea 100%)',
  'linear-gradient(170deg, #86b2ee 6%, #8fc4b4 40%, #e6ac7c 74%, #c08a6a 100%)',
  'linear-gradient(175deg, #f48a9c 6%, #dc8cc4 46%, #9a90ee 100%)',
  'linear-gradient(170deg, #fff6e6 6%, #f6d6c4 52%, #ecbfa8 100%)',
]
/** Who else is in the conversation: four overlapping faces. */
export function PeopleStack() {
  return (
    <span className="flex items-center" aria-hidden>
      {PEOPLE.map((bg, i) => (
        <span key={i} className="block h-4 w-4 rounded-full shadow-[0_0_0_1.5px_var(--color-ws-bg)]" style={{ background: bg, marginLeft: i ? -6.7 : 0, zIndex: i }} />
      ))}
    </span>
  )
}

const MODELS = [
  'linear-gradient(180deg, #f0e0e4 6%, #f4f0ee 46%, #cdeee2 100%)',
  'linear-gradient(180deg, #e0908c 8%, #d4a2c4 52%, #b9a4e6 100%)',
  'linear-gradient(180deg, #b81ed0 6%, #dc22cc 40%, #ffa8ec 100%)',
  'linear-gradient(180deg, #ff9448 8%, #fa9a5c 48%, #ec9a82 100%)',
]
/** "Model Fusion": the models answering together. */
export function ModelStack() {
  return (
    <span className="flex items-center" aria-hidden>
      {MODELS.map((bg, i) => (
        <span key={i} className="block h-4 w-4 rounded-full shadow-[0_0_0_1.5px_var(--color-ws-panel)]" style={{ background: bg, marginLeft: i ? -6.7 : 0, zIndex: i }} />
      ))}
    </span>
  )
}

/* ── line icons ──────────────────────────────────────────────────────── */

/** Quick Chat: a burst of fine rays. */
export function Burst(p: IconProps) {
  return (
    <Line {...p} strokeWidth={1.2}>
      {Array.from({ length: 12 }, (_, i) => (
        <path key={i} d="M12 2.2v8.8" transform={`rotate(${i * 30} 12 12)`} />
      ))}
    </Line>
  )
}

/** Memory: a clock with three notes beside it. */
export function MemoryClock(p: IconProps) {
  return (
    <Line {...p}>
      <circle cx="14.5" cy="12" r="7" />
      <path d="M14.5 8.4V12l2 1.7" />
      <path d="M2.4 7.2h2.4M2.4 12h2.4M2.4 16.8h2.4" />
    </Line>
  )
}

/** Settings and Filter: a nut — a hexagon around a hole. */
export function Nut({ hole = 3, ...p }: IconProps & { hole?: number }) {
  return (
    <Line {...p}>
      <path d="M10.6 2.9a2.8 2.8 0 0 1 2.8 0l5.4 3.1a2.8 2.8 0 0 1 1.4 2.4v7.2a2.8 2.8 0 0 1-1.4 2.4l-5.4 3.1a2.8 2.8 0 0 1-2.8 0L5.2 18a2.8 2.8 0 0 1-1.4-2.4V8.4A2.8 2.8 0 0 1 5.2 6z" />
      <circle cx="12" cy="12" r={hole} />
    </Line>
  )
}

/** Save to the workspace: an arrow dropping into a tray. */
export function TrayDown(p: IconProps) {
  return (
    <Line {...p}>
      <path d="M7.4 7.8H6.2a3 3 0 0 0-3 3v7.4a3 3 0 0 0 3 3h11.6a3 3 0 0 0 3-3v-7.4a3 3 0 0 0-3-3h-1.2" />
      <path d="M12 2.4v13" />
      <path d="m8.2 11.8 3.8 3.8 3.8-3.8" />
    </Line>
  )
}

/** Expand: two opposite corners opening. */
export function Corners(p: IconProps) {
  return (
    <Line {...p}>
      <path d="M13.2 4.6a6.2 6.2 0 0 1 6.2 6.2" />
      <path d="M10.8 19.4a6.2 6.2 0 0 1-6.2-6.2" />
    </Line>
  )
}

/** Home: a house with its doorstep. */
export function HomeStep(p: IconProps) {
  return (
    <Line {...p}>
      <path d="M4.4 10.3 12 4.1l7.6 6.2v7.9a2 2 0 0 1-2 2H6.4a2 2 0 0 1-2-2z" />
      <path d="M9.7 16.2h4.6" />
    </Line>
  )
}

/** Messages: a rounded bubble with three dots. */
export function BubbleDots(p: IconProps) {
  return (
    <Line {...p}>
      <rect x="3.4" y="4.4" width="17.2" height="15.2" rx="5" />
      <path d="M8.4 12h.01M12 12h.01M15.6 12h.01" strokeWidth={2.2} />
    </Line>
  )
}

/** Apps: four round tiles. */
export function Tiles(p: IconProps) {
  return (
    <Line {...p}>
      <circle cx="7.2" cy="7.2" r="3.3" />
      <circle cx="16.8" cy="7.2" r="3.3" />
      <circle cx="7.2" cy="16.8" r="3.3" />
      <circle cx="16.8" cy="16.8" r="3.3" />
    </Line>
  )
}

/** Inbox: a tray. */
export function Tray(p: IconProps) {
  return (
    <Line {...p}>
      <rect x="3.4" y="3.8" width="17.2" height="16.4" rx="4" />
      <path d="M3.6 12.6h4.2l1.4 2.4h5.6l1.4-2.4h4.2" />
    </Line>
  )
}

/** Workflows: three steps handing over to one another. */
export function Steps(p: IconProps) {
  return (
    <Line {...p}>
      <circle cx="12" cy="5.4" r="2.5" />
      <circle cx="5.6" cy="17" r="2.5" />
      <circle cx="18.4" cy="17" r="2.5" />
      <path d="M9.6 6.4a8 8 0 0 0-4.4 7.8M14.4 6.4a8 8 0 0 1 4.4 7.8M8.2 18a8 8 0 0 0 7.6 0" />
    </Line>
  )
}

/** Email: an envelope with a shallow flap. */
export function Envelope(p: IconProps) {
  return (
    <Line {...p}>
      <rect x="3.2" y="5" width="17.6" height="14" rx="3.4" />
      <path d="M3.6 10.2c5.2 2.4 11.6 2.4 16.8 0" />
    </Line>
  )
}

/**
 * Send: a solid paper plane, nose to the upper right, with its fold cut in. One closed shape with
 * round joins (the stroke rounds every corner evenly), so it stays clean at any size.
 */
export function Plane({ size = 16, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden {...rest}>
      {/* The top edge sits on a whole pixel at 16 px, so it stays sharp on a 1× screen. */}
      <path d="M1.75 2.85h12.5L8 13.9 5.8 7.4z" fill="currentColor" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round" />
      <path d="M4.7 8 9.7 5.2" fill="none" stroke="#fff" strokeWidth={1.15} strokeLinecap="round" />
    </svg>
  )
}

/** The small caret after a section label. */
export function Caret() {
  return (
    <svg width="7" height="6" viewBox="0 0 7 6" aria-hidden className="shrink-0">
      <path d="M0.7 0.6h5.6a0.35 0.35 0 0 1 .27.58L4 4.9a.65.65 0 0 1-1 0L.43 1.18A.35.35 0 0 1 .7.6z" fill="currentColor" />
    </svg>
  )
}

/* ── app logos (24 px tiles) ─────────────────────────────────────────── */

export type AppName = 'stripe' | 'slack' | 'gcal' | 'excel'

export function AppLogo({ app }: { app: AppName }) {
  if (app === 'stripe')
    return (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-[#5b49f2]" aria-hidden>
        <svg width="15" height="12.5" viewBox="0 0 14 12">
          <path d="M1.2 3.3a1 1 0 0 1 .8-1L12 .5a.9.9 0 0 1 1 .9v8a1 1 0 0 1-.8 1L2.2 11.5a.9.9 0 0 1-1-.9z" fill="#fff" />
        </svg>
      </span>
    )
  if (app === 'slack')
    return (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-[#351436]" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path d="M5.04 15.12a2.52 2.52 0 1 1-2.52-2.52h2.52zm1.27 0a2.52 2.52 0 0 1 5.04 0v6.32a2.52 2.52 0 1 1-5.04 0z" fill="#e01e5a" />
          <path d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52zm0 1.27a2.52 2.52 0 0 1 0 5.04H2.52a2.52 2.52 0 1 1 0-5.04z" fill="#36c5f0" />
          <path d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52zm-1.27 0a2.52 2.52 0 0 1-5.04 0V2.52a2.52 2.52 0 1 1 5.04 0z" fill="#2eb67d" />
          <path d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52zm0-1.27a2.52 2.52 0 0 1 0-5.04h6.32a2.52 2.52 0 1 1 0 5.04z" fill="#ecb22e" />
        </svg>
      </span>
    )
  if (app === 'gcal')
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px]"
        style={{ background: 'linear-gradient(135deg, #7fdcff 0%, #e9fbff 26%, #ffffff 52%, #bff0ff 76%, #45ccff 100%)' }}
        aria-hidden
      >
        <span className="flex h-[17px] w-[17px] items-center justify-center rounded-[3px] bg-[#4a86f4] text-[10px] font-bold leading-none tracking-[-0.03em] text-white">31</span>
      </span>
    )
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px]"
      style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eafbee 40%, #bff0cb 100%)' }}
      aria-hidden
    >
      <svg width="20" height="19" viewBox="0 0 17 16">
        <rect x="5" y="0.5" width="11.5" height="15" rx="1.6" fill="#21a366" />
        <path d="M10.75.5H15a1.5 1.5 0 0 1 1.5 1.5v2.9h-5.75z" fill="#33c481" />
        <path d="M10.75 4.9h5.75v4.4h-5.75z" fill="#107c41" />
        <path d="M5 9.3h5.75v6.2H6.6A1.6 1.6 0 0 1 5 13.9z" fill="#185c37" />
        <rect x="0.5" y="4" width="8.2" height="8.2" rx="1.3" fill="#107c41" />
        <path d="m2.9 6.1 3.4 4M6.3 6.1l-3.4 4" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" />
      </svg>
    </span>
  )
}

/* A sheet of every hand-drawn icon at a large size — only to check their curves (demo.html?icons). */
export function IconSheet() {
  const line: [string, (p: IconProps) => ReactNode][] = [
    ['Burst', Burst], ['MemoryClock', MemoryClock], ['Nut', Nut], ['TrayDown', TrayDown], ['Corners', Corners], ['HomeStep', HomeStep],
    ['BubbleDots', BubbleDots], ['Tiles', Tiles], ['Tray', Tray], ['Steps', Steps], ['Envelope', Envelope],
  ]
  return (
    <div style={{ background: '#0a0a0a', color: '#9c9c9c', padding: 24, display: 'flex', flexWrap: 'wrap', gap: 24, font: '11px Inter' }}>
      {line.map(([n, C]) => (
        <div key={n} style={{ textAlign: 'center' }}>
          <C size={96} />
          <div>{n}</div>
        </div>
      ))}
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 96, height: 96, background: '#fff', color: '#000', borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Plane size={60} />
        </div>
        <div>Plane</div>
      </div>
      {(['rune', 'aether', 'syntax', 'theo', 'clara'] as AgentName[]).map((a) => (
        <div key={a} style={{ textAlign: 'center' }}>
          <AgentGlyph agent={a} size={96} />
          <div>{a}</div>
        </div>
      ))}
      <div style={{ textAlign: 'center', color: '#939393' }}>
        <div style={{ transform: 'scale(8)', transformOrigin: 'top left', width: 12, height: 12 }}>
          <Caret />
        </div>
      </div>
      {(['stripe', 'slack', 'gcal', 'excel'] as AppName[]).map((a) => (
        <div key={a} style={{ width: 96, height: 96 }}>
          <div style={{ transform: 'scale(4)', transformOrigin: 'top left' }}>
            <AppLogo app={a} />
          </div>
        </div>
      ))}
      <div style={{ width: 96, height: 96 }}><div style={{ transform: 'scale(5)', transformOrigin: 'top left' }}><WorkspaceMark /></div></div>
      <div style={{ width: 96, height: 96 }}><div style={{ transform: 'scale(5)', transformOrigin: 'top left' }}><UserAvatar /></div></div>
      <div style={{ width: 300, height: 96 }}><div style={{ transform: 'scale(4)', transformOrigin: 'top left', display: 'flex', gap: 12 }}><PeopleStack /><ModelStack /></div></div>
    </div>
  )
}
